import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Appointment, Branch, Customer, Device, Room, Staff, Treatment } from '@/lib/types';
import {
  Banner,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  inputStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { SearchInput, matches } from '@/components/SearchInput';
import { CustomerPicker } from '@/components/CustomerPicker';
import { Select } from '@/components/Select';
import { dt } from '@/lib/format';

const BOARD: { stage: Appointment['stage']; label: string; accent: string }[] = [
  { stage: 'BOOKED', label: '已預約', accent: 'var(--stage-1)' },
  { stage: 'CHECKIN', label: '已報到', accent: 'var(--stage-2)' },
  { stage: 'CONSULT', label: '諮詢中', accent: 'var(--stage-3)' },
  { stage: 'TREATING', label: '療程中', accent: 'var(--stage-4)' },
  { stage: 'DONE', label: '已完成', accent: 'var(--stage-5)' },
];

export function BookingPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [open, setOpen] = useState(false);
  const [editAppt, setEditAppt] = useState<Appointment | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [dragOver, setDragOver] = useState<string | null>(null);

  const appts = useQuery({ queryKey: ['appointments'], queryFn: () => api.get<Appointment[]>('/appointments') });
  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });
  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/catalog/rooms') });
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => api.get<Device[]>('/catalog/devices') });
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/catalog/branches') });

  const doctors = (staff.data ?? []).filter((s) => s.role === 'DOCTOR');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['appointments'] });

  const action = useMutation({
    mutationFn: (v: { id: string; verb: string }) => api.post(`/appointments/${v.id}/${v.verb}`),
    onSuccess: () => void invalidate(),
    onError: fail,
  });
  const setStage = useMutation({
    mutationFn: (v: { id: string; stage: string }) => api.post(`/appointments/${v.id}/stage`, { stage: v.stage }),
    onSuccess: () => void invalidate(),
    onError: fail,
  });

  const drop = (stage: string) => {
    const id = dragId;
    setDragOver(null);
    setDragId(null);
    if (id) setStage.mutate({ id, stage });
  };

  const list = appts.data ?? [];
  const cName = (id: string) => customers.data?.find((c) => c.id === id)?.name ?? id;
  const tName = (id: string) => treatments.data?.find((t) => t.id === id)?.name ?? id;
  const dName = (id: string) => doctors.find((d) => d.id === id)?.name ?? '—';
  const board = q ? list.filter((a) => matches(cName(a.customerId), q) || matches(tName(a.treatmentId), q) || matches(dName(a.doctorId), q)) : list;

  return (
    <div>
      <PageHeader
        title="三維預約引擎"
        subtitle="醫師 × 診間 × 設備 三維資源衝突偵測；維護中設備不可排 · 卡片可拖放變更狀態"
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SearchInput value={q} onChange={setQ} placeholder="搜尋顧客 / 療程 / 醫師" />
            <button className="btn btn-primary" onClick={() => { setEditAppt(null); setOpen(true); }}>
              <Icon d={I.plus} size={13} /> 新增預約
            </button>
          </div>
        }
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="今日預約總數" value={String(list.filter((a) => a.stage !== 'CANCELLED').length)} />
        <Stat label="進行中" value={String(list.filter((a) => ['CHECKIN', 'CONSULT', 'TREATING'].includes(a.stage)).length)} tone="primary" />
        <Stat label="已完成" value={String(list.filter((a) => a.stage === 'DONE').length)} tone="success" />
        <Stat label="已取消" value={String(list.filter((a) => a.stage === 'CANCELLED').length)} tone="default" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {BOARD.map((col) => {
          const cards = board.filter((a) => a.stage === col.stage);
          const isOver = dragOver === col.stage;
          return (
            <div
              key={col.stage}
              className="card"
              style={{
                padding: 0,
                overflow: 'hidden',
                outline: isOver ? `2px dashed ${col.accent}` : 'none',
                outlineOffset: -2,
                background: isOver ? 'var(--primary-tint)' : undefined,
                transition: 'background 0.12s',
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragId && dragOver !== col.stage) setDragOver(col.stage);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver((s) => (s === col.stage ? null : s));
              }}
              onDrop={(e) => {
                e.preventDefault();
                drop(col.stage);
              }}
            >
              <div
                style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--line-soft)',
                  borderTop: `3px solid ${col.accent}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{col.label}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }} className="num">
                  {cards.length}
                </span>
              </div>
              <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 80 }}>
                {cards.map((a) => (
                  <div
                    key={a.id}
                    draggable
                    onDragStart={() => setDragId(a.id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setDragOver(null);
                    }}
                    style={{
                      padding: 10,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--line-soft)',
                      borderRadius: 8,
                      borderLeft: `3px solid ${col.accent}`,
                      cursor: 'grab',
                      opacity: dragId === a.id ? 0.5 : 1,
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{cName(a.customerId)}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{tName(a.treatmentId)}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--ink-4)', marginTop: 2 }} className="num">
                      {dt(a.startAt)} · {dName(a.doctorId)}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                      {col.stage !== 'DONE' && (
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '3px 8px', fontSize: 10.5 }}
                          onClick={() => action.mutate({ id: a.id, verb: 'advance' })}
                        >
                          推進 →
                        </button>
                      )}
                      {col.stage !== 'BOOKED' && (
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '3px 8px', fontSize: 10.5 }}
                          onClick={() => action.mutate({ id: a.id, verb: 'regress' })}
                        >
                          ← 回退
                        </button>
                      )}
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '3px 8px', fontSize: 10.5 }}
                        onClick={() => { setEditAppt(a); setOpen(true); }}
                      >
                        編輯
                      </button>
                      {(col.stage === 'BOOKED' || col.stage === 'CHECKIN') && (
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '3px 8px', fontSize: 10.5, color: 'var(--danger)' }}
                          onClick={() => action.mutate({ id: a.id, verb: 'no-show' })}
                          title="標記爽約（累計後將要求訂金）"
                        >
                          爽約
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {cards.length === 0 && (
                  <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-4)', fontSize: 11 }}>—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {list.some((a) => a.stage === 'CANCELLED') && (
        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ink-4)' }}>
          已取消 {list.filter((a) => a.stage === 'CANCELLED').map((a) => cName(a.customerId)).join('、')}
        </div>
      )}

      <BookingModal
        open={open}
        edit={editAppt}
        cName={cName}
        tName={tName}
        onClose={() => { setOpen(false); setEditAppt(null); }}
        treatments={treatments.data ?? []}
        doctors={doctors}
        rooms={rooms.data ?? []}
        devices={devices.data ?? []}
        branches={branches.data ?? []}
        onOk={() => {
          ok(editAppt ? '預約已更新 · 三維衝突檢測通過' : '預約已建立 · 三維衝突檢測通過');
          setOpen(false);
          setEditAppt(null);
          void invalidate();
        }}
        onFail={fail}
      />
    </div>
  );
}

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const blankForm = {
  customerId: '',
  treatmentId: '',
  doctorId: '',
  roomId: '',
  deviceId: '',
  branchId: '',
  startAt: '',
  note: '',
};

function BookingModal({
  open,
  edit,
  cName,
  tName,
  onClose,
  treatments,
  doctors,
  rooms,
  devices,
  branches,
  onOk,
  onFail,
}: {
  open: boolean;
  edit: Appointment | null;
  cName: (id: string) => string;
  tName: (id: string) => string;
  onClose: () => void;
  treatments: Treatment[];
  doctors: Staff[];
  rooms: Room[];
  devices: Device[];
  branches: Branch[];
  onOk: () => void;
  onFail: (e: unknown) => void;
}) {
  const isEdit = !!edit;
  const [form, setForm] = useState(blankForm);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // 開啟時依「編輯/新增」初始化表單
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setForm({
        customerId: edit.customerId,
        treatmentId: edit.treatmentId,
        doctorId: edit.doctorId,
        roomId: edit.roomId ?? '',
        deviceId: edit.deviceId ?? '',
        branchId: edit.branchId ?? '',
        startAt: toLocalInput(edit.startAt),
        note: edit.note ?? '',
      });
    } else {
      setForm(blankForm);
    }
  }, [open, edit]);

  const create = useMutation({
    mutationFn: () => {
      if (edit) {
        return api.patch<Appointment>(`/appointments/${edit.id}`, {
          doctorId: form.doctorId,
          roomId: form.roomId || '',
          deviceId: form.deviceId || '',
          branchId: form.branchId || '',
          startAt: new Date(form.startAt).toISOString(),
          note: form.note,
        });
      }
      return api.post<Appointment>('/appointments', {
        customerId: form.customerId,
        treatmentId: form.treatmentId,
        doctorId: form.doctorId,
        roomId: form.roomId || undefined,
        deviceId: form.deviceId || undefined,
        branchId: form.branchId || undefined,
        startAt: new Date(form.startAt).toISOString(),
        note: form.note || undefined,
      });
    },
    onSuccess: onOk,
    onError: onFail,
  });

  const ready = form.customerId && form.treatmentId && form.doctorId && form.startAt;

  return (
    <Modal open={open} onClose={onClose} width={520}>
      <ModalHeader
        title={isEdit ? '編輯預約' : '新增預約'}
        sub={isEdit ? '可改時間 / 醫師 / 診間 / 設備（自動重新檢測衝突）' : '三維度資源衝突檢測（醫師 × 診間 × 設備）'}
        onClose={onClose}
      />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {isEdit ? (
          <div style={{ padding: 10, background: 'var(--surface-2)', borderRadius: 8, fontSize: 12 }}>
            <span style={{ color: 'var(--ink-3)' }}>顧客 / 療程（不可變更）：</span>
            <span style={{ fontWeight: 600 }}>{cName(form.customerId)} · {tName(form.treatmentId)}</span>
          </div>
        ) : (
          <>
            <Field label="顧客">
              <CustomerPicker value={form.customerId} onChange={(id) => set('customerId', id)} />
            </Field>
            <Field label="療程項目">
              <Select
                value={form.treatmentId}
                onChange={(v) => set('treatmentId', v)}
                placeholder="— 選擇療程 —"
                options={treatments.map((t) => ({ value: t.id, label: `${t.name}（${t.durationMin} 分）` }))}
              />
            </Field>
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="醫師">
            <Select
              value={form.doctorId}
              onChange={(v) => set('doctorId', v)}
              placeholder="— 選擇醫師 —"
              options={doctors.map((d) => ({ value: d.id, label: d.name }))}
            />
          </Field>
          <Field label="開始時間">
            <input type="datetime-local" style={inputStyle} value={form.startAt} onChange={(e) => set('startAt', e.target.value)} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="診間（選填）">
            <Select
              value={form.roomId}
              onChange={(v) => set('roomId', v)}
              placeholder="不指定"
              options={rooms.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Field>
          <Field label="設備（選填）">
            <Select
              value={form.deviceId}
              onChange={(v) => set('deviceId', v)}
              placeholder="不指定"
              options={devices.map((d) => ({ value: d.id, label: `${d.name}${d.status === 'maint' ? '（維護中）' : ''}`, disabled: d.status === 'maint' }))}
            />
          </Field>
        </div>
        <Field label="施作分店（選填，完成時依扣料規則自動扣減該店耗材）">
          <Select
            value={form.branchId}
            onChange={(v) => set('branchId', v)}
            placeholder="不指定"
            options={branches.map((b) => ({ value: b.id, label: `${b.name}（${b.country}）` }))}
          />
        </Field>
        <Field label="備註（可選）">
          <input style={inputStyle} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="例：第一次施作 / 敏感肌" />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>
          取消
        </button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!ready || create.isPending} onClick={() => create.mutate()}>
          <Icon d={I.check} size={13} /> {isEdit ? '檢測並儲存變更' : '檢測並開立預約'}
        </button>
      </div>
    </Modal>
  );
}
